/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";
import SendIcon from "@material-ui/icons/Send";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignMetaModal from "../../components/CampaignMetaModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, FormControl, InputLabel, Select, MenuItem, TablePagination, Pagination, Box } from "@material-ui/core";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS_META") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS_META") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN_META") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const CampaignsMeta = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [planConfigs, setPlanConfigs] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRecurring, setFilterRecurring] = useState("");

  const { datetimeToClient } = useDate();

  useEffect(() => {
    const fetchSession = async () => {
      if (!user?.id || !user?.companyId) return;
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        setPlanConfigs(planConfigs);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [user, history, getPlanCompany]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, filterStatus, filterRecurring]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchCampaigns = async () => {
        try {
          const { data } = await api.get("/campaigns-meta", {
            params: { searchParam, pageNumber, status: filterStatus, isRecurring: filterRecurring },
          });
          dispatch({ type: "LOAD_CAMPAIGNS_META", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, filterStatus, filterRecurring]);

  useEffect(() => {
    const socket = localStorage.getItem("socket");
    const companyId = localStorage.getItem("companyId");

    const onCampaignMetaUpdate = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS_META", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN_META", payload: +data.id });
      }
    };

    if (socket) {
      socket.on(`company-${companyId}-campaignMeta`, onCampaignMetaUpdate);

      return () => {
        socket.off(`company-${companyId}-campaignMeta`, onCampaignMetaUpdate);
      };
    }
  }, []);

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setDeletingCampaign(null);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns-meta/${campaignId}`);
      toast.success(i18n.t("campaignsMeta.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleCancelCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns-meta/${campaignId}/cancel`);
      toast.success("Campanha Meta cancelada!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleRestartCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns-meta/${campaignId}/restart`);
      toast.success("Campanha Meta reiniciada!");
    } catch (err) {
      toastError(err);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns-meta/${campaignId}/pause`);
      toast.success("Campanha Meta pausada!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      await api.post(`/campaigns-meta/${campaignId}/resume`);
      toast.success("Campanha Meta retomada!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleViewReport = (campaignId) => {
    history.push(`/campaigns-meta/${campaignId}/report`);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderStatusChip = (status) => {
    const statusColors = {
      INATIVA: "default",
      PROGRAMADA: "primary",
      EM_ANDAMENTO: "secondary",
      PAUSADA: "default",
      CANCELADA: "default",
      FINALIZADA: "default",
    };

    return (
      <Chip
        label={status}
        color={statusColors[status] || "default"}
        size="small"
      />
    );
  };

  if (!planConfigs || !planConfigs?.plan?.useCampaigns) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <CampaignMetaModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        aria-labelledby="form-dialog-title"
        campaignMetaId={selectedCampaign && selectedCampaign.id}
      />
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaignsMeta.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaignsMeta.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Title>{i18n.t("campaignsMeta.title")} (API Oficial)</Title>
          </Grid>
          <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCampaignModal}
            >
              {i18n.t("campaignsMeta.buttons.add")}
            </Button>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="INATIVA">Inativa</MenuItem>
                <MenuItem value="PROGRAMADA">Programada</MenuItem>
                <MenuItem value="EM_ANDAMENTO">Em Andamento</MenuItem>
                <MenuItem value="CANCELADA">Cancelada</MenuItem>
                <MenuItem value="FINALIZADA">Finalizada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Recorrência</InputLabel>
              <Select
                value={filterRecurring}
                onChange={(e) => setFilterRecurring(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="true">Recorrentes</MenuItem>
                <MenuItem value="false">Não recorrentes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Templates</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Agendamento</TableCell>
              <TableCell align="center">Recorrente</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell align="center">{campaign.id}</TableCell>
                  <TableCell align="center">{campaign.name}</TableCell>
                  <TableCell align="center">
                    {campaign.template1Name}
                    {campaign.template2Name && `, ${campaign.template2Name}`}
                    {campaign.template3Name && `, ${campaign.template3Name}`}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatusChip(campaign.status)}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.isRecurring ? (
                      <Chip icon={<RepeatIcon />} label={campaign.recurrenceType} size="small" color="primary" />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Relatório">
                      <IconButton size="small" onClick={() => handleViewReport(campaign.id)}>
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEditCampaign(campaign)}
                        disabled={campaign.status === "EM_ANDAMENTO" || campaign.status === "PAUSADA"}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {campaign.status === "EM_ANDAMENTO" && (
                      <Tooltip title="Pausar">
                        <IconButton size="small" onClick={() => handlePauseCampaign(campaign.id)}>
                          <PauseCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {campaign.status === "PAUSADA" && (
                      <Tooltip title="Continuar">
                        <IconButton size="small" onClick={() => handleResumeCampaign(campaign.id)}>
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {campaign.status === "PROGRAMADA" && (
                      <Tooltip title="Cancelar">
                        <IconButton size="small" onClick={() => handleCancelCampaign(campaign.id)}>
                          <StopIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(campaign.status === "CANCELADA" || campaign.status === "FINALIZADA") && (
                      <Tooltip title="Reiniciar">
                        <IconButton size="small" onClick={() => handleRestartCampaign(campaign.id)}>
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Deletar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingCampaign(campaign);
                        }}
                        disabled={campaign.status === "EM_ANDAMENTO" || campaign.status === "PAUSADA"}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={7} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default CampaignsMeta;
